import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { api, isInsufficientCredits, type Book, type Job, type Page } from "../api";
import {
  ArrowDown, ArrowUp, BackIcon, CrossIcon, EyeIcon, PlusIcon, RetryIcon, RotateIcon,
} from "../components/icons";
import { ProgressDialog } from "../components/ProgressDialog";

const MAX_PAGES = 10;
const MIN_PAGES = 2;
const MAX_DESCRIPTION = 1000;
const MAX_STORY_BACKGROUND = 1500;

// Tiles for photos the user has picked but not yet uploaded. Picks land here
// as "ready" without touching the network so the picker doesn't block. On
// submit we flip every "ready" tile to "staging", stage all the PUTs to S3 in
// parallel (the bandwidth-heavy step), then commit them one-by-one in pick
// order so the server-assigned `position` matches what the user tapped.
// iOS Safari's photo picker returns the FileList in tap order, which lines up
// with the badges shown in the picker — we just preserve it through commit.
interface Pending {
  id: string;
  // Keep the original File around so submit/retry can upload the bytes
  // without bouncing the user back to the photo picker.
  file: File;
  previewUrl: string;
  status: "ready" | "staging" | "committing" | "error";
  error?: string;
  // Edits applied locally before upload; flushed to the page row via PATCH
  // after commit so the user can annotate/rotate/reorder while bytes are
  // still on the device.
  rotation: number;
  description: string | null;
}

export default function Compose({
  book,
  pages: initialPages,
  onBack,
  onSubmitted,
}: {
  book: Book;
  pages: Page[];
  onBack: () => void;
  onSubmitted: (result: { book: Book; job: Job }) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(book.title);
  const [storyBackground, setStoryBackground] = useState(book.storyBackground ?? "");
  const [pages, setPages] = useState<Page[]>(initialPages);
  const [pending, setPending] = useState<Pending[]>([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [viewing, setViewing] = useState<{ src: string; rotation: number; alt: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch the live balance on mount so the cost summary stays accurate even
  // after the user made a book earlier this session. Null means "unknown"
  // (fetch pending/failed) — we never block submit in that case; the server
  // 402 stays the backstop.
  const [credits, setCredits] = useState<{ balance: number | null; unlimited: boolean; perPage: number } | null>(null);
  useEffect(() => {
    let alive = true;
    void api.me().then((m) => {
      if (alive && m) setCredits({ balance: m.credits?.balance ?? null, unlimited: m.credits?.unlimited ?? false, perPage: m.creditsPerPage ?? 10 });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Object URLs created for pending previews — revoke on unmount so the
  // browser can release the memory once the tiles are gone.
  const previewUrlsRef = useRef<Set<string>>(new Set());
  // AbortController per pending tile, keyed by client-side id. Lets the user
  // cancel an in-flight PUT (the slow bandwidth step) when they realize they
  // picked the wrong photo. We only abort the staging fetch; once a tile
  // reaches "committing" it's a fast DB insert that we let finish.
  const stageAbortersRef = useRef<Map<string, AbortController>>(new Map());
  useEffect(
    () => () => {
      for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
      previewUrlsRef.current.clear();
      for (const ac of stageAbortersRef.current.values()) ac.abort();
      stageAbortersRef.current.clear();
    },
    [],
  );

  // Persist book-level fields on idle (350ms after last change). Avoids a
  // Save button while still letting users leave the screen safely.
  const debounceRef = useRef<number | null>(null);
  const bookSavesInFlightRef = useRef<Set<Promise<unknown>>>(new Set());
  const saveBookDraft = (nextTitle: string, nextStoryBackground: string) => {
    const context = nextStoryBackground.trim();
    const request = api
      .updateBook(book.id, { title: nextTitle, storyBackground: context.length > 0 ? context : null })
      .catch((e) => {
        setErr(String(e));
        throw e;
      });
    const tracked = request.finally(() => {
      bookSavesInFlightRef.current.delete(tracked);
    });
    bookSavesInFlightRef.current.add(tracked);
    return tracked;
  };

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void saveBookDraft(title, storyBackground).catch(() => undefined);
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, storyBackground]);

  const onFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setErr(null);

    // FileList order on iOS Safari is the pick order from the photo picker;
    // we slice it in place so the rest of this function never reorders.
    const remaining = MAX_PAGES - pages.length - pending.length;
    if (remaining <= 0) return;
    const batch = Array.from(files).slice(0, remaining);
    if (batch.length === 0) return;

    const items = batch.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      return {
        id: cryptoRandomId(),
        file,
        previewUrl,
        status: "ready" as const,
        rotation: 0,
        description: null,
      };
    });

    setPending((prev) => [...prev, ...items]);
  };

  // Upload all pending tiles that haven't been committed yet. Stages in
  // parallel (the slow PUT-to-S3 step) and commits sequentially in pick
  // order. We can't parallelize commit because the server assigns
  // `position` from max(position) + 1 with no uniqueness constraint, so
  // concurrent commits would race for the same slot.
  // Returns true iff every pending tile reached `pages`.
  const uploadPending = async (): Promise<boolean> => {
    const items = pending.filter(
      (p) => p.status === "ready" || p.status === "error",
    );
    if (items.length === 0) return pending.length === 0;

    const aborters = new Map<string, AbortController>();
    for (const it of items) {
      const ac = new AbortController();
      aborters.set(it.id, ac);
      stageAbortersRef.current.set(it.id, ac);
    }

    setPending((prev) =>
      prev.map((p) =>
        aborters.has(p.id)
          ? { ...p, status: "staging", error: undefined }
          : p,
      ),
    );

    const staged = await Promise.allSettled(
      items.map((it) =>
        api.stagePageUpload(book.id, it.file, aborters.get(it.id)!.signal),
      ),
    );

    let allOk = true;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const result = staged[i]!;
      const aborter = aborters.get(item.id)!;
      stageAbortersRef.current.delete(item.id);

      // Tile was dismissed mid-upload — it's already gone from `pending`.
      if (aborter.signal.aborted) continue;

      if (result.status === "rejected") {
        allOk = false;
        setPending((prev) =>
          prev.map((p) =>
            p.id === item.id
              ? { ...p, status: "error", error: String(result.reason) }
              : p,
          ),
        );
        continue;
      }

      setPending((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: "committing" } : p)),
      );
      try {
        const page = await api.commitPage(book.id, result.value);
        // Apply edits the user made before submit (rotation + description).
        // The commit endpoint only accepts key/contentType, so we PATCH
        // separately. patchPage returns the full pages list, which keeps
        // ordering and any concurrent state in sync.
        const trimmedDesc = item.description?.trim() ?? "";
        const needsPatch = item.rotation !== 0 || trimmedDesc.length > 0;
        if (needsPatch) {
          const patch: { rotation?: number; description?: string } = {};
          if (item.rotation !== 0) patch.rotation = item.rotation;
          if (trimmedDesc.length > 0) patch.description = trimmedDesc;
          const updated = await api.patchPage(book.id, page.id, patch);
          setPages(updated);
        } else {
          setPages((prev) => [...prev, page]);
        }
        setPending((prev) => prev.filter((p) => p.id !== item.id));
        URL.revokeObjectURL(item.previewUrl);
        previewUrlsRef.current.delete(item.previewUrl);
      } catch (e) {
        allOk = false;
        setPending((prev) =>
          prev.map((p) =>
            p.id === item.id ? { ...p, status: "error", error: String(e) } : p,
          ),
        );
      }
    }

    return allOk;
  };

  const dismissPending = (id: string) => {
    // Abort the in-flight PUT if it's still running. The staging loop sees
    // the aborted signal and skips this tile without showing an error.
    const aborter = stageAbortersRef.current.get(id);
    if (aborter) {
      aborter.abort();
      stageAbortersRef.current.delete(id);
    }
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current.delete(target.previewUrl);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  // Re-run stage + commit for a single tile that previously errored. The
  // retried page lands at the end of the book (server assigns position from
  // max + 1) — pick order is lost, but the user can reorder with the arrows.
  // Blocked while a batch submit is in flight: a concurrent commit here
  // would race the submit loop for the next `position` slot.
  const retryPending = async (id: string) => {
    if (busy) return;
    const target = pending.find((p) => p.id === id);
    if (!target || target.status !== "error") return;

    const aborter = new AbortController();
    stageAbortersRef.current.set(id, aborter);
    setPending((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: "staging", error: undefined } : p)),
    );

    try {
      const staged = await api.stagePageUpload(book.id, target.file, aborter.signal);
      if (aborter.signal.aborted) {
        stageAbortersRef.current.delete(id);
        return;
      }
      stageAbortersRef.current.delete(id);
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: "committing" } : p)));
      const page = await api.commitPage(book.id, staged);
      const trimmedDesc = target.description?.trim() ?? "";
      const needsPatch = target.rotation !== 0 || trimmedDesc.length > 0;
      if (needsPatch) {
        const patch: { rotation?: number; description?: string } = {};
        if (target.rotation !== 0) patch.rotation = target.rotation;
        if (trimmedDesc.length > 0) patch.description = trimmedDesc;
        const updated = await api.patchPage(book.id, page.id, patch);
        setPages(updated);
      } else {
        setPages((prev) => [...prev, page]);
      }
      setPending((prev) => prev.filter((p) => p.id !== id));
      URL.revokeObjectURL(target.previewUrl);
      previewUrlsRef.current.delete(target.previewUrl);
    } catch (e) {
      stageAbortersRef.current.delete(id);
      // Swallow aborts — dismissPending already removed the tile.
      if (aborter.signal.aborted) return;
      setPending((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: "error", error: String(e) } : p)),
      );
    }
  };

  const move = async (pageId: string, dir: -1 | 1) => {
    const i = pages.findIndex((p) => p.id === pageId);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= pages.length) return;
    const target = pages[j]!.position;

    // Optimistic swap
    const next = pages.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    setPages(next.map((p, idx) => ({ ...p, position: idx + 1 })));

    try {
      const fresh = await api.patchPage(book.id, pageId, { position: target });
      setPages(mergeDescriptionDrafts(fresh));
    } catch (e) {
      setErr(String(e));
    }
  };

  const remove = async (pageId: string) => {
    const prev = pages;
    const pendingDescriptionSave = descDebounceRef.current.get(pageId);
    if (pendingDescriptionSave) window.clearTimeout(pendingDescriptionSave);
    descDebounceRef.current.delete(pageId);
    descriptionDraftsRef.current.delete(pageId);
    setPages(pages.filter((p) => p.id !== pageId).map((p, i) => ({ ...p, position: i + 1 })));
    try {
      await api.deletePage(book.id, pageId);
    } catch (e) {
      setErr(String(e));
      setPages(prev);
      const restored = prev.find((p) => p.id === pageId);
      if (restored) descriptionDraftsRef.current.set(pageId, restored.description ?? "");
    }
  };

  // Description edits are debounced per-page so typing doesn't fire a PATCH
  // on every keystroke. We persist by id rather than by index because pages
  // can be reordered while a debounce is in flight.
  const descDebounceRef = useRef<Map<string, number>>(new Map());
  const descSavesInFlightRef = useRef<Set<Promise<unknown>>>(new Set());
  const descriptionDraftsRef = useRef<Map<string, string>>(
    new Map(initialPages.map((p) => [p.id, p.description ?? ""])),
  );
  const mergeDescriptionDrafts = (fresh: Page[]) =>
    fresh.map((p) =>
      descriptionDraftsRef.current.has(p.id)
        ? { ...p, description: descriptionDraftsRef.current.get(p.id)! }
        : p,
    );
  const saveDescription = (pageId: string, value: string) => {
    const trimmed = value.trim();
    const request = api
      .patchPage(book.id, pageId, { description: trimmed.length === 0 ? null : trimmed })
      .catch((e) => {
        setErr(String(e));
        throw e;
      });
    const tracked = request.finally(() => {
      descSavesInFlightRef.current.delete(tracked);
    });
    descSavesInFlightRef.current.add(tracked);
    return tracked;
  };

  const setDescription = (pageId: string, value: string) => {
    descriptionDraftsRef.current.set(pageId, value);
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, description: value } : p)),
    );
    const timers = descDebounceRef.current;
    const existing = timers.get(pageId);
    if (existing) window.clearTimeout(existing);
    const t = window.setTimeout(() => {
      timers.delete(pageId);
      void saveDescription(pageId, value).catch(() => undefined);
    }, 500);
    timers.set(pageId, t);
  };

  useEffect(
    () => () => {
      for (const t of descDebounceRef.current.values()) window.clearTimeout(t);
      descDebounceRef.current.clear();
    },
    [],
  );

  const flushDraftEdits = async () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    for (const t of descDebounceRef.current.values()) window.clearTimeout(t);
    descDebounceRef.current.clear();

    await Promise.allSettled([
      ...bookSavesInFlightRef.current,
      ...descSavesInFlightRef.current,
    ]);

    await Promise.all([
      saveBookDraft(title, storyBackground),
      ...pages.map((p) => saveDescription(p.id, descriptionDraftsRef.current.get(p.id) ?? p.description ?? "")),
    ]);
  };

  // Reorder pending tiles among themselves before they're uploaded. Pending
  // tiles always commit at the end of `pages` in their current array order,
  // so swapping here directly controls the position the server will assign.
  const movePending = (id: string, dir: -1 | 1) => {
    setPending((prev) => {
      const i = prev.findIndex((p) => p.id === id);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  };

  const rotatePending = (id: string) => {
    setPending((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, rotation: (p.rotation + 90) % 360 } : p,
      ),
    );
  };

  const setPendingDescription = (id: string, value: string) => {
    setPending((prev) =>
      prev.map((p) => (p.id === id ? { ...p, description: value } : p)),
    );
  };

  const rotate = async (pageId: string) => {
    const page = pages.find((p) => p.id === pageId);
    if (!page) return;
    const nextRotation = (page.rotation + 90) % 360;
    setPages(pages.map((p) => (p.id === pageId ? { ...p, rotation: nextRotation } : p)));
    try {
      await api.patchPage(book.id, pageId, { rotation: nextRotation });
    } catch (e) {
      setErr(String(e));
    }
  };

  const submit = async (intent?: "new" | "replace") => {
    // Close the confirm sheet right away so the user can watch the per-tile
    // upload progress underneath instead of staring at a frozen sheet.
    setShowSubmit(false);
    setBusy(true);
    setErr(null);
    try {
      await flushDraftEdits();
      if (pending.length > 0) {
        const ok = await uploadPending();
        if (!ok) {
          setErr(t("compose.uploadFailed"));
          setBusy(false);
          return;
        }
      }
      const result = intent ? await api.submitPhotos(book.id, intent) : await api.submitBook(book.id);
      onSubmitted(result);
    } catch (e) {
      setErr(isInsufficientCredits(e) ? e.message : String(e));
      setShowSubmit(false);
      setBusy(false);
    }
  };

  const totalTiles = pages.length + pending.length;
  const cost = totalTiles * (credits?.perPage ?? 10);
  const balance = credits?.balance ?? 0;
  const cantAfford = credits != null && !credits.unlimited && cost > balance;
  const hasErrors = pending.some((p) => p.status === "error");
  // Pending tiles count toward MIN_PAGES because they're uploaded as part of
  // submit. We still block submit if any tile is in an error state — the user
  // has to retry or remove it first.
  const canSubmit =
    totalTiles >= MIN_PAGES && title.trim().length > 0 && !busy && !hasErrors;

  return (
    <div className="tp-app tp-wide">
      <div className="tp-topbar">
        <button className="tp-topbtn" onClick={onBack} aria-label={t("common.back")}>
          <BackIcon />
        </button>
        <div className="tp-eyebrow">{t("compose.eyebrowDraft")}</div>
        <div style={{ width: 38 }} />
      </div>

      <div className="tp-screen" style={{ paddingTop: 12 }}>
        <div className="tp-compose-cols">
          <div>
        {/* Title */}
        <div style={{ marginBottom: 22 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>{t("compose.titleLabel")}</label>
          <input
            className="tp-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("compose.titlePlaceholder")}
            maxLength={200}
          />
          {book.author.name && (
            <div className="tp-body" style={{ fontSize: 12, color: "var(--tp-ink-soft)", marginTop: 6 }}>
              <Trans
                i18nKey="compose.byline"
                values={{
                  name: book.author.name,
                  agePart: book.author.age != null ? t("compose.agePart", { age: book.author.age }) : "",
                }}
                components={{
                  editLink: (
                    <a href="/app/account" style={{ color: "var(--tp-ink-soft)", textDecoration: "underline" }} />
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Story context — book-level background that informs polish for every
         * page. Lives above the page grid so authors can set the frame before
         * reviewing individual pages. */}
        <div style={{ marginBottom: 22 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>{t("compose.storyContextLabel")}</label>
          <textarea
            className="tp-page-desc tp-story-bg"
            value={storyBackground}
            onChange={(e) => setStoryBackground(e.target.value)}
            placeholder={t("compose.storyContextPlaceholder")}
            rows={3}
            maxLength={MAX_STORY_BACKGROUND}
          />
        </div>

        {/* Pages */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <label className="tp-eyebrow">{t("compose.pagesLabel", { count: totalTiles, max: MAX_PAGES })}</label>
            <span className="tp-body" style={{ fontSize: 12, color: "var(--tp-ink-soft)" }}>{t("compose.reorderHint")}</span>
          </div>

          <div className="tp-grid-2">
            {pages.map((p, i) => (
              <PageCard
                key={p.id}
                page={p}
                index={i}
                total={pages.length}
                onUp={() => move(p.id, -1)}
                onDown={() => move(p.id, 1)}
                onRemove={() => remove(p.id)}
                onRotate={() => rotate(p.id)}
                onView={() =>
                  setViewing({
                    src: p.originalUrl ?? "",
                    rotation: p.rotation,
                    alt: t("compose.pageAlt", { num: p.position }),
                  })
                }
                onDescription={(v) => setDescription(p.id, v)}
              />
            ))}
            {pending.map((p, i) => (
              <PendingCard
                key={p.id}
                pending={p}
                displayIndex={pages.length + i}
                canMoveUp={i > 0}
                canMoveDown={i < pending.length - 1}
                onMoveUp={() => movePending(p.id, -1)}
                onMoveDown={() => movePending(p.id, 1)}
                onRotate={() => rotatePending(p.id)}
                onView={() =>
                  setViewing({
                    src: p.previewUrl,
                    rotation: p.rotation,
                    alt: t("compose.pageAlt", { num: pages.length + i + 1 }),
                  })
                }
                onDescription={(v) => setPendingDescription(p.id, v)}
                onDismiss={() => dismissPending(p.id)}
                onRetry={() => void retryPending(p.id)}
              />
            ))}
            {totalTiles < MAX_PAGES && (
              <button
                type="button"
                className="tp-thumb tp-thumb-add"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <PlusIcon />
                <span>{pages.length === 0 ? t("compose.addPages") : t("compose.addMore")}</span>
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            // Intentionally omit image/heic|heif: iOS Safari only auto-
            // transcodes captured photos to JPEG when HEIC isn't an accepted
            // type. Listing it makes the browser hand us raw HEIC bytes,
            // which <img> can't render — the user sees a broken thumbnail.
            accept="image/png,image/jpeg,image/webp"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              onFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
          </div>

          <div>
        <div className="tp-eyebrow" style={{ display: "block", marginBottom: 6 }}>{t("compose.readyToMake")}</div>
        <div className="tp-body" style={{ fontSize: 13, color: "var(--tp-ink-soft)", marginBottom: 14 }}>
          {t("compose.readyDescription")}
        </div>

        {err && <div className="tp-error">{err}</div>}

        <button
          className="tp-btn tp-btn-primary tp-btn-block"
          onClick={() => setShowSubmit(true)}
          disabled={!canSubmit}
        >
          {busy ? t("compose.makingBook") : t("compose.makeMyBook")}
        </button>
        <div className="tp-body" style={{ textAlign: "center", fontSize: 12, color: "var(--tp-ink-soft)", marginTop: 8 }}>
          {t("compose.makeHelp")}
        </div>
          </div>
        </div>
      </div>

      {viewing && (
        <Lightbox
          src={viewing.src}
          rotation={viewing.rotation}
          alt={viewing.alt}
          onClose={() => setViewing(null)}
        />
      )}

      {showSubmit && (
        <div
          className="tp-sheet-overlay"
          onClick={() => {
            if (!busy) setShowSubmit(false);
          }}
        >
          <div className="tp-sheet" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
              <div style={{ width: 40, height: 4, borderRadius: 999, background: "var(--tp-ink-soft)", opacity: 0.3 }} />
            </div>
            <div className="tp-h1" style={{ marginBottom: 8 }}>{t("compose.confirmTitle")}</div>
            <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 18, fontSize: 15 }}>
              {t("compose.confirmBody")}
            </div>
            {credits && (
              credits.unlimited ? (
                <div
                  className="tp-body"
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "2px solid var(--tp-ink)",
                    background: "var(--tp-surface-alt)",
                    fontSize: 13,
                  }}
                >
                  {t("credits.unlimited")}
                </div>
              ) : cantAfford ? (
                <div className="tp-error" style={{ marginBottom: 14 }}>
                  {t("credits.notEnough", { cost, balance })}
                </div>
              ) : (
                <div
                  className="tp-body"
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "2px solid var(--tp-ink)",
                    background: "var(--tp-surface-alt)",
                    fontSize: 13,
                  }}
                >
                  <div>{t("credits.available", { balance })}</div>
                  <div style={{ color: "var(--tp-ink-soft)", marginTop: 2 }}>
                    {t("credits.bookCost", { cost, pages: totalTiles, perPage: credits.perPage })}
                  </div>
                </div>
              )
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {book.revisionOf ? (
                <>
                  <button className="tp-btn tp-btn-primary tp-btn-block" onClick={() => submit("replace")} disabled={busy || cantAfford}>
                    {busy ? t("compose.sending") : t("editBook.replaceOriginal")}
                  </button>
                  <button className="tp-btn tp-btn-block" onClick={() => submit("new")} disabled={busy || cantAfford}>
                    {t("editBook.saveAsNew")}
                  </button>
                  <div className="tp-body tp-soft" style={{ fontSize: 13, textAlign: "center" }}>{t("editBook.replaceHint")}</div>
                </>
              ) : (
                <button className="tp-btn tp-btn-primary tp-btn-block" onClick={() => submit()} disabled={busy || cantAfford}>
                  {busy ? t("compose.sending") : t("compose.confirmYes")}
                </button>
              )}
              <button className="tp-btn tp-btn-block" onClick={() => setShowSubmit(false)} disabled={busy}>
                {t("compose.keepEditing")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProgressDialog
        open={busy}
        title={t("compose.progressTitle")}
        body={t("compose.progressBody")}
      />
    </div>
  );
}

function Lightbox({
  src, rotation, alt, onClose,
}: {
  src: string;
  rotation: number;
  alt: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="tp-lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <img
        className="tp-lightbox-img"
        src={src}
        alt={alt}
        style={{ transform: `rotate(${rotation}deg)` }}
        onClick={(e) => e.stopPropagation()}
      />
      <button className="tp-lightbox-close" onClick={onClose} aria-label={t("compose.close")}>
        <CrossIcon />
      </button>
    </div>
  );
}

function PendingCard({
  pending, displayIndex, canMoveUp, canMoveDown,
  onMoveUp, onMoveDown, onRotate, onView, onDescription, onDismiss, onRetry,
}: {
  pending: Pending;
  displayIndex: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRotate: () => void;
  onView: () => void;
  onDescription: (value: string) => void;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const isError = pending.status === "error";
  const isReady = pending.status === "ready";
  // Show the full PageCard-like controls before upload starts and after it
  // fails, so the user can keep annotating/reordering until they're happy
  // and so retry sits beside a working remove button. Hide them during
  // staging/committing — the bytes are in flight and a server-side rotation
  // or reorder would race the commit loop.
  const showFullControls = isReady || isError;
  // Hide the dismiss/retry row only during `committing` — a fast DB insert
  // we don't want to interrupt. Ready/staging/error tiles all get a remove
  // button so the user can back out before or during upload.
  const showDismiss = pending.status !== "committing";
  const busyText =
    pending.status === "staging" ? t("compose.uploading")
    : pending.status === "committing" ? t("common.saving")
    : isError ? t("compose.uploadFailedShort")
    : null;
  return (
    <div className="tp-page-card">
      <div className={`tp-thumb${isError ? " tp-thumb-error" : ""}`} aria-busy={!isError && !isReady}>
        <img
          src={pending.previewUrl}
          alt={isReady ? t("compose.pageAlt", { num: displayIndex + 1 }) : t("compose.uploadingPageAlt")}
          style={{ transform: `rotate(${pending.rotation}deg)` }}
        />
        {busyText && (
          <div className="tp-thumb-pending" role="status">{busyText}</div>
        )}
        {showDismiss && (
          <div className="tp-thumb-remove" style={{ display: "flex", gap: 4 }}>
            {showFullControls && (
              <button className="tp-thumb-iconbtn" onClick={onView} aria-label={t("compose.viewLarger")}>
                <EyeIcon />
              </button>
            )}
            {isError && (
              <button
                className="tp-thumb-iconbtn"
                onClick={onRetry}
                aria-label={t("compose.retryUpload")}
              >
                <RetryIcon />
              </button>
            )}
            <button
              className="tp-thumb-iconbtn"
              onClick={onDismiss}
              aria-label={isError ? t("compose.removeFailedUpload") : isReady ? t("compose.remove") : t("compose.cancelUpload")}
            >
              <CrossIcon />
            </button>
          </div>
        )}
        {showFullControls && (
          <div className="tp-thumb-actions">
            <div style={{ display: "flex", gap: 4 }}>
              <button className="tp-thumb-iconbtn" onClick={onMoveUp} disabled={!canMoveUp} aria-label={t("compose.moveUp")}><ArrowUp /></button>
              <button className="tp-thumb-iconbtn" onClick={onMoveDown} disabled={!canMoveDown} aria-label={t("compose.moveDown")}><ArrowDown /></button>
              <button className="tp-thumb-iconbtn" onClick={onRotate} aria-label={t("compose.rotate")}><RotateIcon /></button>
            </div>
          </div>
        )}
        <div className="tp-thumb-num">{displayIndex + 1}</div>
      </div>
      {showFullControls && (
        <textarea
          className="tp-page-desc"
          value={pending.description ?? ""}
          onChange={(e) => onDescription(e.target.value)}
          placeholder={t("compose.descriptionPlaceholder")}
          rows={2}
          maxLength={MAX_DESCRIPTION}
        />
      )}
      {isError && pending.error && (
        <div className="tp-error" style={{ fontSize: 12 }}>{pending.error}</div>
      )}
    </div>
  );
}

// Cheap random id for client-side tracking of pending tiles. Falls back to
// Math.random when crypto.randomUUID is missing (very old browsers); these
// ids never leave the client.
function cryptoRandomId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function PageCard({
  page, index, total, onUp, onDown, onRemove, onRotate, onView, onDescription,
}: {
  page: Page; index: number; total: number;
  onUp: () => void; onDown: () => void; onRemove: () => void; onRotate: () => void;
  onView: () => void;
  onDescription: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="tp-page-card">
      <div className="tp-thumb">
        <img
          src={page.originalUrl ?? ""}
          alt={t("compose.pageAlt", { num: index + 1 })}
          style={{ transform: `rotate(${page.rotation}deg)` }}
        />
        <div className="tp-thumb-remove" style={{ display: "flex", gap: 4 }}>
          <button className="tp-thumb-iconbtn" onClick={onView} aria-label={t("compose.viewLarger")}><EyeIcon /></button>
          <button className="tp-thumb-iconbtn" onClick={onRemove} aria-label={t("compose.remove")}><CrossIcon /></button>
        </div>
        <div className="tp-thumb-actions">
          <div style={{ display: "flex", gap: 4 }}>
            <button className="tp-thumb-iconbtn" onClick={onUp} disabled={index === 0} aria-label={t("compose.moveUp")}><ArrowUp /></button>
            <button className="tp-thumb-iconbtn" onClick={onDown} disabled={index === total - 1} aria-label={t("compose.moveDown")}><ArrowDown /></button>
            <button className="tp-thumb-iconbtn" onClick={onRotate} aria-label={t("compose.rotate")}><RotateIcon /></button>
          </div>
        </div>
        <div className="tp-thumb-num">{index + 1}</div>
      </div>
      <textarea
        className="tp-page-desc"
        value={page.description ?? ""}
        onChange={(e) => onDescription(e.target.value)}
        placeholder={t("compose.descriptionPlaceholder")}
        rows={2}
        maxLength={MAX_DESCRIPTION}
      />
    </div>
  );
}
